import { EndpointBuilder } from '@reduxjs/toolkit/query';
import {
  FetchArgs,
  FetchBaseQueryError,
  FetchBaseQueryMeta
} from '@reduxjs/toolkit/query';
import { BaseQueryFn } from '@reduxjs/toolkit/query';
import {
  DownloadCSVResponse,
  PushEventResponse,
  PushEventResponseModified
} from '../../types';
import { DownloadCSVRecord } from '../../types/records/download-csv';
import { AllQueryParams } from '../../types/other';

export const pushEventsEndpoints = (
  builder: EndpointBuilder<
    BaseQueryFn<
      string | FetchArgs,
      unknown,
      FetchBaseQueryError,
      NonNullable<unknown>,
      FetchBaseQueryMeta
    >,
    string,
    'hesApi'
  >
) => ({
  getPushEvents: builder.query<
    PushEventResponseModified,
    { searchQuery: string }
  >({
    query: ({ searchQuery }) => ({
      url: `push-data/push-events${searchQuery}`
    }),
    keepUnusedDataFor: 0,
    transformResponse: (
      response: PushEventResponse
    ): PushEventResponseModified => {
      // Flattening the response into PushEventsRecordModified format
      const flattenedRecords =
        response.data.records &&
        response.data.records.flatMap((record) => {
          // Extracting common properties
          const commonFields = {
            device_identifier: record.device_identifier,
            meter_RTC: record.data.meter_time,
            push_triggered_time: record.data.push_triggered_time,
            'HES Received Time': record.data_timestamp,
            data: {
              data_type: record.data.data_type,
              device_id: record.data.device_id,
              event_bits: record.data.event_bits,
              push_counter: record.data.push_counter,
              push_obis: record.data.push_obis
            }
          };

          // Handling multiple event messages
          return record.data.event_message.map(
            (eventMsg: { index: number; message: string }) => ({
              ...commonFields,
              index_value: eventMsg.index,
              event_message: eventMsg.message
            })
          );
        });

      return {
        ...response,
        data: {
          ...response.data,
          records: flattenedRecords
        }
      };
    }
  }),
  downloadAlarmsData: builder.query<DownloadCSVRecord, AllQueryParams>({
    query: (params) => ({
      url: `/push-data/push-events/download-report`,
      params: params,
      responseHandler: async (response) => {
        const contentType = response.headers.get('content-type');

        // If the content type is JSON, parse the response as JSON
        if (contentType?.includes('application/json')) {
          return await response.json(); // Parse and return JSON
        }

        // Otherwise, treat it as a Blob (for files like CSV)
        return await response.blob(); // Handle as Blob (binary data)
      }
    }),
    transformResponse: (
      response: Blob | DownloadCSVResponse,
      meta
    ): DownloadCSVRecord => {
      const contentType = meta?.response?.headers?.get('content-type');
      console.log(response);
      console.log(contentType);

      // Check if the content type is CSV or text, indicating a file download
      if (
        contentType?.includes('text/csv') ||
        contentType?.includes('application/octet-stream')
      ) {
        // If the response is a Blob (CSV file), return it as a csvBlob
        return { csvBlob: response as Blob };
      }

      // If it's a JSON response, handle it as DownloadCSVResponse
      if ('data' in response) {
        const downloadResponse = response as DownloadCSVResponse;
        const url = downloadResponse?.data?.records[0]?.url;
        return { url };
      }

      // If neither case matches, throw an error or handle accordingly
      throw new Error('Unexpected response format');
    }
  })
});
